import logger from '~/services/logger';
import { getRandomValues } from './helper';
import type { JW_OBJ, JW_URI } from './types';
import { DATA_EVENTS } from './types';

export async function getJWPLTX(): Promise<JW_OBJ> {
  const o: any = {};

  // Hostname for sending analytics
  const URL = 'https://ihe.jwpltx.com/v1/jwplayer6/ping.gif?';

  const sdk = 'web';
  const platformName = 'Web';

  // Query parameters for sending analytics
  const URI = {
    pss: 1, // Need to fix it, if we will use play list items
    oos: platformName,
    oosv: platformName,
    sdk: sdk,
  };

  // There are anywhere between 1 to 128 quantiles in any given video, 128 is max for every video
  const MAX_DURATION_IN_QUANTILES = 128;
  // query params instance.
  let uri: JW_URI;
  // Time watched after last t event was sent
  let timeWatched = 0;
  // Last progress of the video stored
  let lastVp = 0;
  // Next quantile to send t event
  let nextQuantile: number;
  // Seeking state, gets reset after 1 sec of the latest seeked event
  let isSeeking = false;
  // Interval for Live Streams seeking state
  let liveInterval: string | number | NodeJS.Timeout | null | undefined = null;
  // Timeout for seeking state
  let seekingTimeout: string | number | NodeJS.Timeout | null | undefined =
    null;

  // Here we convert seconds watched to quantiles, the units accepted by the analytics service (res is 0 - 128)
  function getCurrentProgressQuantile(progress: number, duration: number) {
    return Math.floor(MAX_DURATION_IN_QUANTILES * (progress / duration));
  }

  // We convert `q` metric to quantiles (0 - 128) to define next breakpoint for `t` event
  function getNextTriggerQuantile(progress: number, duration: number) {
    return (
      (Math.ceil((progress / duration) * uri.q) * MAX_DURATION_IN_QUANTILES) /
      uri.q
    );
  }

  // Sends the last ping when closing the player or window
  function sendRemainingData() {
    if (uri.pw === -1) {
      clearLiveInterval();
    } else {
      const pw = getCurrentProgressQuantile(lastVp, uri.vd);
      uri.pw = pw;
    }

    clearSeekingTimeout();

    if (timeWatched) {
      uri.ti = Math.floor(timeWatched);
      timeWatched = 0;
      // @ts-ignore
      sendData('gab');
    }
  }

  // We set interval for sending t events for live streams where we can't get progress info
  function setLiveInterval() {
    liveInterval = setInterval(() => {
      timeWatched += 1;
    }, 1000);
  }

  // We clear interval after complete or when seeking
  function clearLiveInterval() {
    clearInterval(liveInterval as NodeJS.Timeout);
    liveInterval = null;
  }

  // There is currently a 1 sec debounce surrounding seeking event in order to logically group multiple `seeked` events
  function setSeekingTimeout() {
    seekingTimeout = setTimeout(() => {
      isSeeking = false;

      // Set new timeout when seeked event reached for live events
      if (uri.pw === -1 && !liveInterval) {
        setLiveInterval();
      }

      // @ts-ignore
      sendData('vs');
    }, 1000);
  }

  // We clear timeout after complete event or when new seeking events are received
  function clearSeekingTimeout() {
    clearTimeout(seekingTimeout as NodeJS.Timeout);
    seekingTimeout = null;
  }

  // Process a player ready event
  // @ts-ignore
  o.ready = function (aid, bun, fed, id, t, oosv, av, oaid) {
    uri = JSON.parse(JSON.stringify(URI));
    uri.aid = aid;
    uri.bun = bun;
    uri.fed = fed;
    uri.id = id;
    uri.t = t;
    uri.oosv = oosv;
    uri.av = av;
    uri.emi = generateId(12);
    uri.pli = generateId(12);
    uri.ppid = 2;
    uri.oaid = oaid;
    sendData(DATA_EVENTS.setup);
  };

  // Process an ad impression event
  o.adImpression = function () {
    sendData(DATA_EVENTS.impression);
  };

  // Process seek event
  // @ts-ignore
  o.seek = function (offset, duration) {
    isSeeking = true;
    clearSeekingTimeout();

    // Clear interval in case of a live stream not to update time watched while seeking
    if (uri.pw === -1) {
      clearLiveInterval();
    } else {
      // We need to rewrite progress of the video when seeking to have a valid ti param
      lastVp = offset;
      nextQuantile = getNextTriggerQuantile(offset, duration);
    }
  };

  // Process seeked event
  // @ts-ignore
  o.seeked = function () {
    setSeekingTimeout();
  };

  // When player is disconnected from the page -> send the rest of the data and cancel possible intervals
  // @ts-ignore
  o.remove = function () {
    sendRemainingData();
  };

  // Send the rest of the data and cancel possible intervals in case a web page is closed while watching
  // window.addEventListener('beforeunload', () => {
  //   sendRemainingData();
  // });

  // Process a time tick event
  o.time = function (vp: number, vd: number) {
    if (isSeeking || !uri) {
      return;
    }

    // 0 or negative vd means live stream
    if (vd < 1) {
      // Initial tick means play() event
      if (!uri.pw) {
        uri.vd = 0;
        uri.q = 0;
        uri.pw = -1;

        sendData(DATA_EVENTS.play);
        setLiveInterval();
        // monitor ticks for 20s elapsed
      } else {
        if (timeWatched > 19) {
          const maxTi = 19 * 2; // allow 2x slack
          if (timeWatched > maxTi) {
            logger.info(
              `timeWatched on live is larger than expected: timeWatched: ${timeWatched}, expected: ${maxTi}`
            );
            timeWatched = maxTi;
          }

          uri.ti = timeWatched;
          timeWatched = 0;
          sendData(DATA_EVENTS.quantile);
        }
      }

      // positive vd means  VOD stream
    } else {
      // Initial tick means play() event
      if (!uri.vd) {
        uri.vd = Math.round(vd);

        if (vd < 30) {
          uri.q = 1;
        } else if (vd < 60) {
          uri.q = 4;
        } else if (vd < 180) {
          uri.q = 8;
        } else if (vd < 300) {
          uri.q = 16;
        } else {
          uri.q = 32;
        }

        uri.ti = 0;
        uri.pw = 0;

        // Initialize latest quantile to compare further quantiles with
        nextQuantile = getNextTriggerQuantile(vp, vd);
        // Initial values to compare watched progress
        lastVp = vp;

        sendData(DATA_EVENTS.play);

        // monitor ticks for entering new quantile
      } else {
        const pw = getCurrentProgressQuantile(vp, vd);
        const quantile = getNextTriggerQuantile(vp, vd);

        // Total time watched since last t event.
        timeWatched = timeWatched + (vp - lastVp);
        lastVp = vp;

        if (pw >= nextQuantile) {
          const maxTi = vd / uri.q + 5; // allow 5 seconds slack

          if (timeWatched > maxTi) {
            logger.info(
              `timeWatched is larger than expected: timeWatched: ${timeWatched}, expected: ${maxTi}, q; ${uri.q}, video duration : ${vd}`
            );
            timeWatched = maxTi;
          }

          uri.ti = Math.round(timeWatched);
          uri.pw = pw;

          sendData(DATA_EVENTS.quantile);

          nextQuantile = quantile;
          timeWatched = 0;
        }
      }
    }
  };

  // Process a video complete events
  o.complete = function () {
    // Clear intervals for live streams
    if (uri.pw === -1) {
      clearLiveInterval();
    } else {
      uri.pw = MAX_DURATION_IN_QUANTILES;
    }

    clearSeekingTimeout();

    uri.ti = Math.floor(timeWatched);
    timeWatched = 0;

    // @ts-ignore
    sendData('gab');
  };

  // Helper function to generate IDs

  function generateId(len: number) {
    let arr = new Uint8Array((len || 40) / 2);
    // @ts-ignore
    arr = getRandomValues(arr);

    return Array.from(arr, dec2hex).join('');
  }

  function dec2hex(dec: { toString: (arg0: number) => string }) {
    return dec.toString(16).padStart(2, '0');
  }

  // Serialize and send data to JW Player
  function sendData(type: DATA_EVENTS) {
    uri.e = type;
    uri.sa = Date.now();
    // Serialize data
    let str = '';

    for (const key in uri) {
      // @ts-ignore
      if (uri[key] !== null) {
        // @ts-ignore
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions, no-unused-expressions
        str === '' ? null : (str += '&');
        // @ts-ignore
        str += key + '=' + encodeURIComponent(uri[key]);
      }
    }

    // Ads are sent to special bucket
    let url = URL;

    if (uri.e === DATA_EVENTS.impression) {
      url = URL.replace('jwplayer6', 'clienta');
    }

    // Send data if analytics token is set
    if (uri.aid) {
      const urlToPost = url + str;

      const result = navigator.sendBeacon(urlToPost);

      if (result) {
        logger.info(`sendData: navigator received beacon ${urlToPost}`);
      } else {
        logger.info(
          `sendData: navigator failed to send analytics beacon ${urlToPost}`
        );
      }

      return;
    }
  }

  return o;
}
